import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Mail, Trash2, Phone, MapPin, Star, Copy, Send, Loader2, StickyNote, BookMarked, Check, Flame, MailOpen, Eye, Pencil, X, ExternalLink, ShieldCheck, Globe } from "lucide-react";
import { EmailPreviewDialog } from "@/components/EmailPreviewDialog";

const STAGES = [
  { key: "nouveaux", label: "Nouveaux" },
  { key: "contactes", label: "Contactés" },
  { key: "rdv", label: "RDV planifié" },
  { key: "gagnes", label: "Gagnés" },
  { key: "perdus", label: "Perdus" },
];

const MSG_TYPES = [
  { key: "email", label: "Email de prospection" },
  { key: "call_script", label: "Script d'appel" },
  { key: "relance", label: "Email de relance" },
  { key: "offer", label: "Offre spéciale" },
];

const TONES = ["professionnel", "chaleureux", "direct", "technique"];
const MODELS = [
  { key: "claude-sonnet-5", label: "Claude Sonnet 5" },
  { key: "gpt-5.6-terra", label: "GPT 5.6 Terra" },
];

export default function ProspectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [aiParams, setAiParams] = useState({ message_type: "email", tone: "professionnel", model: "claude-sonnet-5", custom_context: "" });
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedAi, setSelectedAi] = useState(null);
  const [emailSubject, setEmailSubject] = useState("PC UP — Solution dépannage informatique pour votre entreprise");
  const [emailLoading, setEmailLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [mailtoLoading, setMailtoLoading] = useState(false);
  const [smtpChannel, setSmtpChannel] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  const enrichOne = async () => {
    setEnriching(true);
    setEnrichResult(null);
    try {
      const r = await api.post(`/prospects/${id}/enrich`);
      setEnrichResult(r.data);
      const a = r.data.applied;
      if (a.email) toast.success(`Email trouvé : ${a.email}`);
      else if (a.website || a.phone) toast.success("Coordonnées complétées");
      else toast.info("Aucun email public trouvé — privilégiez l'appel");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur recherche");
    } finally {
      setEnriching(false);
    }
  };
  useEffect(() => { api.get("/settings/smtp").then((r) => setSmtpChannel(r.data.configured && r.data.enabled ? r.data.username : null)).catch(() => {}); }, []);
  const COMPANY_MAILBOX = "contact@pcup.fr";

  const canSendIntegrated = (pr) => !!pr?.email_consent || ["rdv", "gagnes"].includes(pr?.stage);

  const sendFromMailbox = async () => {
    if (!selectedAi) return toast.error("Générez d'abord un message");
    const subject = emailSubject.trim();
    const body = selectedAi.content;
    const href = `mailto:${encodeURIComponent(p.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    try { await navigator.clipboard.writeText(body); } catch {}
    window.location.href = href;
    setMailtoLoading(true);
    try {
      const r = await api.post("/emails/mailto-log", { prospect_id: id, subject, ai_message_id: selectedAi.ai_message_id });
      toast.success(`Message ouvert dans votre messagerie (${COMPANY_MAILBOX}) — texte copié. Relance créée pour le ${new Date(r.data.task.due_date).toLocaleDateString("fr-FR")}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setMailtoLoading(false);
    }
  };

  const toggleConsent = async (val) => {
    try {
      await api.patch(`/prospects/${id}`, { email_consent: val });
      toast.success(val ? "Email marqué comme communiqué par le prospect" : "Consentement retiré");
      load();
    } catch {
      toast.error("Erreur mise à jour");
    }
  };

  const startEdit = () => { setEditText(selectedAi.content); setEditing(true); };
  const saveEdit = async () => {
    if (!editText.trim()) return toast.error("Le message ne peut pas être vide");
    setEditSaving(true);
    try {
      const r = await api.patch(`/ai-messages/${selectedAi.ai_message_id}`, { content: editText });
      setSelectedAi(r.data);
      setEditing(false);
      toast.success("Message modifié");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setEditSaving(false);
    }
  };
  const selectAi = (m) => { setEditing(false); setSelectedAi(m); };

  const openPreview = async () => {
    if (!selectedAi) return toast.error("Générez d'abord un message");
    if (!p.email) return toast.error("Email du prospect manquant");
    setPreview(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const r = await api.post("/emails/preview", { prospect_id: id, subject: emailSubject, ai_message_id: selectedAi.ai_message_id });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur aperçu");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const savedTplIds = templates.map((t) => t.source_ai_message_id);

  const load = () => api.get(`/prospects/${id}`).then((r) => setData(r.data)).catch(() => nav("/prospects"));
  const loadTemplates = () => api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); loadTemplates(); /* eslint-disable-next-line */ }, [id]);

  const saveAsTemplate = async () => {
    if (!selectedAi) return;
    setTplLoading(true);
    try {
      const r = await api.post("/templates", { ai_message_id: selectedAi.ai_message_id });
      toast.success(`Modèle « ${r.data.name } » sauvegardé`);
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur sauvegarde modèle");
    } finally {
      setTplLoading(false);
    }
  };

  const applyTemplate = async (templateId) => {
    if (!templateId) return;
    setTplLoading(true);
    try {
      const r = await api.post(`/templates/${templateId}/apply`, { prospect_id: id });
      setSelectedAi(r.data);
      toast.success("Modèle appliqué et personnalisé");
      load();
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur application modèle");
    } finally {
      setTplLoading(false);
    }
  };

  if (!data) return <div className="p-8 text-slate-500 font-mono text-sm">Chargement…</div>;

  const p = data.prospect;

  const changeStage = async (stage) => {
    await api.patch(`/prospects/${id}`, { stage });
    toast.success("Étape mise à jour");
    load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/prospects/${id}/notes`, { content: noteText });
    setNoteText("");
    toast.success("Note ajoutée");
    load();
  };

  const deleteNote = async (nid) => {
    await api.delete(`/notes/${nid}`);
    load();
  };

  const generate = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/generate", { prospect_id: id, ...aiParams });
      setSelectedAi(r.data);
      toast.success("Message généré");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur IA");
    } finally {
      setAiLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!selectedAi) return toast.error("Générez d'abord un message");
    if (!p.email) return toast.error("Email du prospect manquant");
    setEmailLoading(true);
    try {
      await api.post("/emails/send", { prospect_id: id, subject: emailSubject, ai_message_id: selectedAi.ai_message_id });
      toast.success(`Email envoyé à ${p.email}`);
      setPreviewOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi");
    } finally {
      setEmailLoading(false);
    }
  };

  const deleteProspect = async () => {
    if (!confirm("Supprimer ce prospect ?")) return;
    await api.delete(`/prospects/${id}`);
    toast.success("Supprimé");
    nav("/prospects");
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1500px]">
      <button data-testid="back-button" onClick={() => nav("/prospects")} className="text-sm text-slate-400 hover:text-cyan-400 flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Pipeline
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: prospect info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h1 data-testid="prospect-company" className="font-heading text-2xl font-bold text-slate-100 leading-tight">{p.company}</h1>
                {p.contact_name && <p className="text-slate-400 text-sm mt-0.5">{p.contact_name}</p>}
              </div>
              {p.rating > 0 && (
                <div className="flex items-center gap-1 text-sm text-amber-400 shrink-0">
                  <Star className="w-4 h-4 fill-amber-400" />
                  <span className="font-mono">{p.rating}</span>
                </div>
              )}
            </div>

            <div className="mt-5 space-y-2 text-sm">
              {p.phone && <div className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4 text-slate-500" />{p.phone}</div>}
              {p.email && <div className="flex items-center gap-2 text-slate-300"><Mail className="w-4 h-4 text-slate-500" />{p.email}{p.email_source === "website" && <span className="text-[10px] font-mono uppercase text-emerald-400">site web</span>}</div>}
              {p.website && <a data-testid="prospect-website-link" href={p.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 break-all"><Globe className="w-4 h-4 text-slate-500 shrink-0" />{p.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</a>}
              {(p.city || p.address) && <div className="flex items-center gap-2 text-slate-300"><MapPin className="w-4 h-4 text-slate-500" />{p.address} {p.city}</div>}
            </div>

            <div className="mt-5 pt-4 border-t border-slate-800">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Étape</div>
              <select
                data-testid="stage-select"
                value={p.stage}
                onChange={(e) => changeStage(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 outline-none focus:border-cyan-500/60"
              >
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>

            {p.email_opens > 0 && (
              <div data-testid="hot-prospect-banner" className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-2">
                <Flame className="w-4 h-4 text-amber-400 shrink-0" />
                <div className="text-xs text-amber-200">
                  <span className="font-semibold">Prospect chaud</span> · {p.email_opens} ouverture{p.email_opens > 1 ? "s" : ""} d'email
                  {p.last_opened_at && <span className="text-amber-400/70"> · dernière le {new Date(p.last_opened_at).toLocaleString("fr-FR")}</span>}
                </div>
              </div>
            )}

            {p.estimated_value > 0 && (
              <div className="mt-4 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                <div className="text-xs font-mono uppercase tracking-widest text-emerald-400">Valeur estimée</div>
                <div className="font-heading text-2xl font-bold text-emerald-400 mt-1">{Number(p.estimated_value).toLocaleString("fr-FR")} €</div>
              </div>
            )}

            {(p.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.tags.map((t, i) => (
                  <span key={i} className="text-xs font-mono px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">{t}</span>
                ))}
              </div>
            )}

            <button data-testid="enrich-prospect-button" onClick={enrichOne} disabled={enriching} className="mt-4 w-full py-2 rounded-lg text-sm bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />} {p.email ? "Vérifier site web / coordonnées" : "Trouver l'email (site web)"}
            </button>
            {enrichResult && (
              <div data-testid="enrich-result" className="mt-2 text-xs text-slate-400 bg-slate-900/60 border border-slate-800 rounded-lg p-3 space-y-1">
                {enrichResult.result.checked_website ? <div>Site analysé : <span className="text-slate-200 break-all">{enrichResult.result.checked_website}</span>{enrichResult.result.website_found && <span className="text-emerald-400"> (découvert)</span>}</div> : <div>Aucun site web trouvé pour cette entreprise.</div>}
                {enrichResult.result.emails.length > 0 ? <div>Emails trouvés : <span className="text-emerald-300">{enrichResult.result.emails.join(", ")}</span></div> : enrichResult.result.checked_website && <div>Aucun email publié sur le site (souvent un formulaire de contact).</div>}
                {enrichResult.result.phone && <div>Téléphone : <span className="text-slate-200">{enrichResult.result.phone}</span></div>}
              </div>
            )}

            <button data-testid="delete-prospect-button" onClick={deleteProspect} className="mt-4 w-full py-2 rounded-lg text-sm text-rose-400 hover:bg-rose-500/10 border border-slate-800 hover:border-rose-500/30 transition-colors flex items-center justify-center gap-2">
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          </div>

          {/* Notes */}
          <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl">
            <div className="flex items-center gap-2 mb-4">
              <StickyNote className="w-4 h-4 text-amber-400" />
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Notes</div>
            </div>
            <textarea
              data-testid="note-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows="3"
              placeholder="Ajouter une note (appel du 12/03, prospect intéressé…)"
              className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60"
            />
            <button data-testid="add-note-button" onClick={addNote} className="mt-2 w-full py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-medium transition-colors">
              Ajouter
            </button>

            <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
              {(data.notes || []).map((n) => (
                <div key={n.note_id} data-testid={`note-${n.note_id}`} className="p-3 rounded-lg bg-slate-900/60 border border-slate-800 group">
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{n.content}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] font-mono uppercase text-slate-500">{new Date(n.created_at).toLocaleString("fr-FR")}</span>
                    <button onClick={() => deleteNote(n.note_id)} className="opacity-0 group-hover:opacity-100 text-xs text-rose-400 hover:text-rose-300">
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
              {(data.notes || []).length === 0 && <div className="text-xs font-mono text-slate-600 text-center py-4">Aucune note</div>}
            </div>
          </div>
        </div>

        {/* Right: AI + emails */}
        <div className="lg:col-span-2 space-y-4">
          <div className="p-6 bg-[#121215] border border-purple-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <div className="text-xs font-mono uppercase tracking-widest text-purple-300">Générateur IA</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Type</label>
                <select
                  data-testid="ai-type-select"
                  value={aiParams.message_type}
                  onChange={(e) => setAiParams({ ...aiParams, message_type: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 outline-none focus:border-purple-500/60 text-sm"
                >
                  {MSG_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Ton</label>
                <select
                  data-testid="ai-tone-select"
                  value={aiParams.tone}
                  onChange={(e) => setAiParams({ ...aiParams, tone: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 outline-none focus:border-purple-500/60 text-sm"
                >
                  {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Modèle IA</label>
                <select
                  data-testid="ai-model-select"
                  value={aiParams.model}
                  onChange={(e) => setAiParams({ ...aiParams, model: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 outline-none focus:border-purple-500/60 text-sm"
                >
                  {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
            </div>

            <textarea
              data-testid="ai-context-input"
              value={aiParams.custom_context}
              onChange={(e) => setAiParams({ ...aiParams, custom_context: e.target.value })}
              rows="2"
              placeholder="Contexte additionnel (optionnel) : événement récent, angle spécifique…"
              className="mt-3 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-purple-500/60"
            />

            <button
              data-testid="ai-generate-button"
              onClick={generate}
              disabled={aiLoading}
              className="mt-3 w-full py-2.5 rounded-lg bg-purple-500 hover:bg-purple-400 text-slate-950 font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4" /> Générer</>}
            </button>

            {templates.length > 0 && (
              <div className="mt-3 flex items-center gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <BookMarked className="w-4 h-4 text-amber-400 shrink-0" />
                <select
                  data-testid="apply-template-select"
                  value=""
                  disabled={tplLoading}
                  onChange={(e) => applyTemplate(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 outline-none focus:border-amber-500/60 text-sm disabled:opacity-60"
                >
                  <option value="">Appliquer un modèle sauvegardé en un clic…</option>
                  {templates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>{`${t.name} (${t.usage_count} util.)`}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedAi && (
              <div className="mt-5 p-4 bg-slate-900/60 border border-slate-800 rounded-lg fade-up">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
                    {selectedAi.message_type} · {selectedAi.tone} · {selectedAi.model}{selectedAi.edited && <span data-testid="ai-edited-badge" className="ml-2 text-amber-400">· modifié</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    {!editing && (
                      <button data-testid="edit-ai-button" onClick={startEdit} className="text-xs text-slate-300 hover:text-white flex items-center gap-1">
                        <Pencil className="w-3 h-3" /> Modifier
                      </button>
                    )}
                    {!selectedAi.template_id && (
                      savedTplIds.includes(selectedAi.ai_message_id) ? (
                        <span data-testid="template-saved-badge" className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Modèle sauvegardé</span>
                      ) : (
                        <button
                          data-testid="save-template-button"
                          onClick={saveAsTemplate}
                          disabled={tplLoading}
                          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 disabled:opacity-60"
                        >
                          <BookMarked className="w-3 h-3" /> Sauver comme modèle
                        </button>
                      )
                    )}
                    <button
                      data-testid="copy-ai-button"
                      onClick={() => { navigator.clipboard.writeText(selectedAi.content); toast.success("Copié"); }}
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Copier
                    </button>
                  </div>
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <textarea
                      data-testid="ai-edit-textarea"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={Math.min(28, Math.max(10, editText.split("\n").length + 2))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-purple-500/40 text-slate-100 text-sm font-mono leading-relaxed outline-none focus:border-purple-400"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-500">Les astérisques et placeholders sont nettoyés automatiquement à l'enregistrement.</span>
                      <div className="flex gap-2">
                        <button data-testid="cancel-edit-button" onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg text-xs text-slate-300 hover:bg-slate-800 border border-slate-800 flex items-center gap-1"><X className="w-3 h-3" /> Annuler</button>
                        <button data-testid="save-edit-button" onClick={saveEdit} disabled={editSaving} className="px-3 py-1.5 rounded-lg text-xs bg-purple-500 hover:bg-purple-400 text-slate-950 font-semibold flex items-center gap-1 disabled:opacity-60">
                          {editSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Enregistrer
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div data-testid="ai-output" className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed font-mono">{selectedAi.content}</div>
                )}

                {!editing && (selectedAi.message_type === "email" || selectedAi.message_type === "relance" || selectedAi.message_type === "offer") && p.email && (
                  <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">
                    <div className="text-xs font-mono uppercase tracking-widest text-cyan-400">Envoyer l'email</div>
                    <input
                      data-testid="email-subject-input"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Objet"
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60"
                    />

                    <button
                      data-testid="send-from-mailbox-button"
                      onClick={sendFromMailbox}
                      disabled={mailtoLoading}
                      className="w-full py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {mailtoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Envoyer depuis ma messagerie ({COMPANY_MAILBOX})
                    </button>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Ouvre votre messagerie OVH/Gmail/Outlook avec destinataire, objet et texte pré-remplis (texte aussi copié dans le presse-papiers). L'email part réellement de {COMPANY_MAILBOX}. Le prospect passe en « Contactés » et une relance est créée à J+3.
                    </p>

                    <div className={`rounded-lg border p-3 ${canSendIntegrated(p) ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-800 bg-slate-900/40"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-xs text-slate-400">
                          <div className="font-mono uppercase tracking-widest text-slate-500 mb-1">Envoi intégré (avec suivi d'ouverture){preview?.channel && preview.channel !== "platform" ? ` · depuis ${preview.channel}` : ""}</div>
                          Réservé aux échanges transactionnels : prospect qui vous a communiqué son email ou déjà en RDV / Gagné.
                        </div>
                        <label className="flex items-center gap-2 text-xs text-slate-300 shrink-0 cursor-pointer">
                          <input data-testid="email-consent-checkbox" type="checkbox" checked={!!p.email_consent} onChange={(e) => toggleConsent(e.target.checked)} className="accent-emerald-500" />
                          Email communiqué par le prospect
                        </label>
                      </div>
                      {canSendIntegrated(p) ? (
                        <div className="flex gap-2 mt-3">
                          <button
                            data-testid="preview-email-button"
                            onClick={openPreview}
                            className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-medium text-sm transition-colors flex items-center justify-center gap-2"
                          >
                            <Eye className="w-4 h-4" /> Aperçu
                          </button>
                          <button
                            data-testid="send-email-button"
                            onClick={sendEmail}
                            disabled={emailLoading}
                            className="flex-[2] py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                          >
                            {emailLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><ShieldCheck className="w-4 h-4" /> Envoyer {smtpChannel ? `depuis ${smtpChannel}` : "via la plateforme"}</>}
                          </button>
                        </div>
                      ) : (
                        <div data-testid="integrated-send-locked" className="mt-2 text-[11px] text-slate-500">Cochez la case ci-dessus si le prospect vous a donné son email (réponse, carte de visite, formulaire) pour débloquer l'envoi intégré.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Previous AI messages */}
            {(data.ai_messages || []).length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Historique IA</div>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {data.ai_messages.map((m) => (
                    <button
                      key={m.ai_message_id}
                      data-testid={`ai-history-${m.ai_message_id}`}
                      onClick={() => selectAi(m)}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${selectedAi?.ai_message_id === m.ai_message_id ? "border-purple-500/50 bg-purple-500/10" : "border-slate-800 bg-slate-900/40 hover:border-slate-700"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300 font-medium">{m.message_type} · {m.tone}</span>
                        <span className="font-mono text-slate-500">{m.model}</span>
                      </div>
                      <div className="text-slate-500 truncate mt-1">{m.content.slice(0, 90)}…</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Emails sent */}
          {(data.emails || []).length > 0 && (
            <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Emails envoyés</div>
              <div className="space-y-2">
                {data.emails.map((e) => (
                  <div key={e.email_log_id} data-testid={`email-log-${e.email_log_id}`} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-200 truncate">{e.subject}</div>
                      <div className="text-xs text-slate-500 truncate">à {e.recipient} · {new Date(e.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span data-testid="email-sent-badge" className={`text-xs font-mono px-2 py-0.5 rounded-full border ${e.status === "mailbox" ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/20" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"}`}>{e.status === "mailbox" ? "via ma messagerie" : e.channel && e.channel !== "platform" ? `${e.status} · ${e.channel}` : e.status}</span>
                      {e.opened && (
                        <span data-testid="email-opened-badge" className="text-xs font-mono px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1" title={e.last_opened_at ? `Dernière ouverture : ${new Date(e.last_opened_at).toLocaleString("fr-FR")}` : ""}>
                          <MailOpen className="w-3 h-3" /> ouvert{e.open_count > 1 ? ` ×${e.open_count}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} preview={preview} loading={previewLoading} sending={emailLoading} onSend={sendEmail} />
    </div>
  );
}
